/**
 * Service Page - Background service for Amazon Connect 3rd-party application type "Service".
 *
 * Listens for new contacts and launches the agent app for any incoming contact.
 */

import { useEffect, useState } from "react";
import type {
  ContactClearedHandler,
  ContactConnectedHandler,
  ContactStartingAcwHandler,
} from "@amazon-connect/contact";
import {
  authenticateConnect,
  createLiveKitRoom,
  postContactState,
} from "../lib/api";
import { getAttrValue, getInstanceArn } from "../lib/connect-utils";
import { isAllowlistedAgent } from "../lib/agent";
import { trackEvent, trackError, setIdentityContext } from "../lib/analytics";
import { useAppContext } from "../contexts/AppContext";

export default function ServicePage() {
  const [isEmbedded, setIsEmbedded] = useState<boolean | null>(null);
  const [serviceStatus, setServiceStatus] = useState<string>("Initializing...");
  const {
    contactClient,
    agentClient,
    appController,
    sdkError,
    isSdkInitializing,
  } = useAppContext();

  // Check if page is embedded in iframe
  useEffect(() => {
    setIsEmbedded(window.self !== window.top);
  }, []);

  useEffect(() => {
    if (isEmbedded === null) return;

    if (!isEmbedded) {
      setServiceStatus("Not embedded - service inactive");
      return;
    }

    if (sdkError) {
      setServiceStatus(`Service failed: ${sdkError}`);
      return;
    }

    if (isSdkInitializing || !contactClient || !agentClient || !appController) {
      setServiceStatus("Waiting for Connect SDK...");
      return;
    }

    let cancelled = false;
    let listenersRegistered = false;
    let handleConnected: ContactConnectedHandler | undefined;
    let handleStartingAcw: ContactStartingAcwHandler | undefined;
    let handleCleared: ContactClearedHandler | undefined;

    const initializeService = async () => {
      try {
        setServiceStatus("Initializing service...");

        // Stamp the console with the deployed build so a screenshot of
        // the user's devtools is enough to verify they're on the latest
        // calltree-webapps. Mirrors the same fields into PostHog/RUM
        // via service_initialized below.
        console.log(
          `[CalltreeService] build ${__APP_VERSION__} (${__BUILD_SHA__}) committed ${__BUILD_DATE__}`,
        );
        console.log(
          "[CalltreeService] Service using initialized Connect SDK clients",
        );

        // Stamp PostHog/RUM with the agent ARN as a super-property so
        // every subsequent event from this iframe carries agent_arn
        // automatically. Best-effort — falls through silently on
        // workspaces where getARN isn't available yet.
        try {
          const agentArn = await agentClient.getARN();
          if (cancelled) return;
          if (agentArn) setIdentityContext({ agentArn });
        } catch (err) {
          if (cancelled) return;
          console.warn("[CalltreeService] getARN at startup failed:", err);
        }

        // Get app catalog and find the agent app
        let appArn: string | null = null;
        try {
          const apps = await appController.getAppCatalog();
          if (cancelled) return;
          console.info("[CalltreeService] apps", { apps });
          const calltreeApp = apps.find(
            (app) =>
              app.namespace === "calltree-agent-app" ||
              app.namespace === "Calltree-app",
          );
          if (calltreeApp) {
            appArn = calltreeApp.arn;
            console.log(
              "[CalltreeService] Found calltree-agent-app ARN:",
              appArn,
            );
          } else {
            console.warn(
              "[CalltreeService] calltree-agent-app not found in app catalog",
            );
          }
        } catch (error) {
          if (cancelled) return;
          console.error("[CalltreeService] Failed to get app catalog:", error);
          trackError(
            error instanceof Error
              ? error
              : new Error("Failed to get app catalog"),
          );
        }

        // Launch the agent app for a contact if criteria are met
        const launchAppForContact = async (contactId: string) => {
          if (!appArn) {
            console.warn(
              "[CalltreeService] Cannot launch app: ARN not available",
            );
            trackEvent("app_launch_skipped", {
              contactId,
              reason: "app_not_found",
            });
            return;
          }

          try {
            console.log(
              "[CalltreeService] Launching app for contact:",
              contactId,
            );

            await appController.launchApp(appArn, {
              scope: {
                type: "contact",
                contactId,
              },
              parameters: undefined,
              launchKey: contactId,
            });

            console.log("[CalltreeService] App launched successfully");
            trackEvent("app_launched", { contactId });
          } catch (error) {
            console.error("[CalltreeService] Failed to launch app:", error);
            trackError(
              error instanceof Error
                ? error
                : new Error("Failed to launch app"),
            );
          }
        };

        // LiveKit per-contact room lifecycle.
        //
        // /service is a long-lived background iframe and doesn't have a
        // JWT sitting in storage like the per-contact AgentApp iframe
        // does. To call POST /api/livekit/room (which requires
        // Authorization), we mint a service-side JWT here using the
        // same HMAC signature pattern AgentApp.tsx uses: read
        // calltree_agent_app_signature from contact attributes,
        // exchange at /api/auth/connect, store the resulting JWT in
        // /service's own sessionStorage. Then call createLiveKitRoom.
        //
        // Channel (voice / chat / task) is included in the room name
        // (`{customer}:{channel}:{contactId}`), so we resolve it via
        // contactClient.getChannelType and remember it for onCleared
        // since onCleared callbacks don't surface it.
        const channelByContact = new Map<string, string>();

        const resolveChannel = async (contactId: string): Promise<string> => {
          try {
            const ct = await contactClient.getChannelType(contactId);
            return (ct?.type ?? "voice").toLowerCase();
          } catch {
            return "voice";
          }
        };

        const ensureLiveKitRoom = async (contactId: string) => {
          try {
            const agentArn = await agentClient.getARN();
            if (!agentArn) {
              console.warn("[CalltreeService] no agent ARN");
              trackEvent("livekit_room_skipped", {
                contactId,
                reason: "no_agent_arn",
              });
              return;
            }
            // Gate room creation + transcript streaming + copilot
            // dispatch on the BPO-agent allowlist. Non-allowlisted
            // agents skip the entire LiveKit lifecycle — saves
            // worker dispatches on contacts no one will ever
            // monitor. Server-side has no parallel allowlist;
            // dropping lazy-create-on-RoomNotFound in the kinesis
            // consumer (livekit_room.send_transcript) closes the
            // back door so transcripts can't quietly resurrect a
            // room we never created.
            if (!isAllowlistedAgent(agentArn)) {
              console.log(
                "[CalltreeService] agent not allowlisted; skipping LiveKit room for contact",
                contactId,
              );
              trackEvent("livekit_room_skipped", {
                contactId,
                reason: "not_allowlisted",
              });
              return;
            }

            const attrs = await contactClient.getAttributes(contactId, "*");
            const signature = getAttrValue(attrs.calltree_agent_app_signature);
            if (!signature) {
              console.warn(
                "[CalltreeService] no calltree_agent_app_signature on contact",
                contactId,
              );
              trackEvent("livekit_room_skipped", {
                contactId,
                reason: "no_signature",
              });
              return;
            }
            const instanceArn = getInstanceArn(agentArn);
            const channel = await resolveChannel(contactId);
            channelByContact.set(contactId, channel);

            // Customer identifiers pulled from Connect contact attributes
            // for the tools-ui session sidebar label. The 3P SDK's
            // getAttributes() exposes only custom attributes (not Connect
            // system fields), so we fall through a channel-specific list
            // of names the contact flow may have set:
            //   - voice : CustomerPhoneNumber → OriginalPhoneNumber → CustomerEndpoint
            //   - chat / task / email : email → customerEmail
            // Empty strings drop straight through to the contact-id
            // fallback in the sidebar — no harm done.
            const isTextChannel =
              channel === "chat" || channel === "task" || channel === "email";
            const customerEndpoint = isTextChannel
              ? getAttrValue(attrs.email) ||
                getAttrValue(attrs.customerEmail) ||
                ""
              : getAttrValue(attrs.CustomerPhoneNumber) ||
                getAttrValue(attrs.OriginalPhoneNumber) ||
                getAttrValue(attrs.CustomerEndpoint) ||
                "";
            const customerName =
              getAttrValue(attrs.customerName) ||
              getAttrValue(attrs.CustomerName) ||
              "";

            // Mirror AgentApp.tsx auth flow: instance ARN first, fall
            // back to agent ARN. authenticateConnect persists the token
            // in sessionStorage, which createLiveKitRoom reads.
            console.log(
              "[CalltreeService] authenticating Connect for LiveKit room",
              { contactId, channel, instanceArn },
            );
            trackEvent("livekit_auth_attempted", {
              contactId,
              channel,
              instanceArn,
            });
            try {
              await authenticateConnect({
                signature,
                callerArn: instanceArn,
                contactId,
              });
              trackEvent("livekit_auth_succeeded", {
                contactId,
                callerArnKind: "instance",
              });
            } catch (instanceErr) {
              console.warn(
                "[CalltreeService] auth via instance ARN failed; falling back to agent ARN",
                instanceErr,
              );
              try {
                await authenticateConnect({
                  signature,
                  callerArn: agentArn,
                  contactId,
                });
                trackEvent("livekit_auth_succeeded", {
                  contactId,
                  callerArnKind: "agent_fallback",
                });
              } catch (agentErr) {
                trackEvent("livekit_auth_failed", {
                  contactId,
                  error:
                    agentErr instanceof Error
                      ? agentErr.message
                      : String(agentErr),
                });
                throw agentErr;
              }
            }

            const { room_name } = await createLiveKitRoom({
              contactId,
              agentArn,
              instanceId: instanceArn,
              channel,
              customerEndpoint,
              customerName,
            });
            console.log("[CalltreeService] LiveKit room ready:", room_name);
            trackEvent("livekit_room_created", {
              contactId,
              channel,
              roomName: room_name,
            });
          } catch (err) {
            console.warn("[CalltreeService] LiveKit room create failed:", err);
            trackEvent("livekit_room_create_failed", {
              contactId,
              error: err instanceof Error ? err.message : String(err),
            });
            trackError(
              err instanceof Error
                ? err
                : new Error("LiveKit room create failed"),
            );
          }
        };

        // Helper: POST a contact-state transition to the agent-app
        // server which fans out via LiveKit RoomServiceClient. We
        // route through the server rather than firing directly
        // from the iframe because Connect Agent Workspace's CSP
        // forbids cross-origin POSTs to arbitrary LiveKit hosts.
        const notifyContactState = async (
          contactId: string,
          type: "starting_acw" | "cleared",
        ) => {
          const channel = channelByContact.get(contactId) ?? "voice";
          try {
            await postContactState(contactId, type, Date.now(), channel);
            trackEvent(`contact_state_${type}`, { contactId, channel });
          } catch (err) {
            console.warn(
              `[CalltreeService] postContactState(${type}) failed for ${contactId}:`,
              err,
            );
            trackEvent(`contact_state_${type}_failed`, {
              contactId,
              channel,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        };

        // Listen for contact connected events
        handleConnected = async (data) => {
          console.log("[CalltreeService] Contact connected:", data.contactId);
          // Attach contactId as a super-property before any per-contact
          // trackEvent fires, so it shows up on all subsequent events
          // (LiveKit lifecycle, app launch, etc.) without each call site
          // having to pass it explicitly.
          setIdentityContext({ contactId: data.contactId });
          trackEvent("contact_connected", { contactId: data.contactId });
          // Run room create + app launch in parallel — neither blocks the other.
          await Promise.all([
            ensureLiveKitRoom(data.contactId),
            launchAppForContact(data.contactId),
          ]);
        };

        // Call ended → agent entered ACW. We publish the
        // transition so AI Search can stop its elapsed-time
        // timer and the copilot worker can record an LLM-
        // visible "Contact ended" turn in chat history. Room
        // itself stays alive (see onCleared note).
        handleStartingAcw = async (data) => {
          const contactId = data?.contactId;
          if (!contactId) return;
          if (!channelByContact.has(contactId)) return;
          console.log("[CalltreeService] contact starting ACW:", contactId);
          await notifyContactState(contactId, "starting_acw");
        };

        // On contact end we deliberately do NOT delete the LiveKit room.
        // Supervisors complained that AI Search lost the chat session
        // the moment a call cleared (mid-ACW or shortly after) — they
        // want a grace window to keep reviewing the conversation and
        // ask follow-up questions of the copilot.
        //
        // Lifecycle now:
        //   - Supervisor stays in the room as long as their tab is
        //     open and the session is active in their sidebar.
        //   - Worker disconnects after `WORKER_IDLE_DRAIN_SECONDS` of
        //     no STANDARD participant (configured in worker.py).
        //   - Once both leave, LiveKit's `departure_timeout` (30 min,
        //     set in livekit_room.create_room) GCs the room.
        // We still clear local state and analytics context (the
        // contact itself is over) AND notify the room that ACW
        // ended so the copilot worker records the transition.
        handleCleared = async (data) => {
          const contactId = data?.contactId;
          if (!contactId) return;
          if (!channelByContact.has(contactId)) return;
          const channel = channelByContact.get(contactId) ?? "voice";
          // Fire the contact-state notify BEFORE we drop the
          // channel entry — notifyContactState reads it.
          await notifyContactState(contactId, "cleared");
          channelByContact.delete(contactId);
          // Clear the per-contact super-property — keeps the
          // identity strictly current so events emitted between
          // contacts don't get attributed to a stale id.
          setIdentityContext({ contactId: null });
          console.log(
            "[CalltreeService] contact cleared:",
            contactId,
            channel,
            "(room left intact; LiveKit will GC after timeouts)",
          );
          trackEvent("contact_cleared", { contactId, channel });
        };

        if (cancelled) return;

        contactClient.onConnected(handleConnected);
        contactClient.onStartingAcw(handleStartingAcw);
        contactClient.onCleared(handleCleared);
        listenersRegistered = true;

        setServiceStatus("Service running - listening for contacts");
        trackEvent("service_initialized", {
          app_version: __APP_VERSION__,
          build_sha: __BUILD_SHA__,
          build_date: __BUILD_DATE__,
        });
      } catch (error) {
        if (cancelled) return;
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error("[CalltreeService] Initialization failed:", error);
        setServiceStatus(`Service failed: ${errorMessage}`);
        trackError(
          error instanceof Error
            ? error
            : new Error("Service initialization failed"),
        );
      }
    };

    initializeService();

    return () => {
      cancelled = true;
      if (!listenersRegistered) return;
      if (handleConnected) contactClient.offConnected(handleConnected);
      if (handleStartingAcw) {
        contactClient.offStartingAcw(handleStartingAcw);
      }
      if (handleCleared) contactClient.offCleared(handleCleared);
    };
  }, [
    agentClient,
    appController,
    contactClient,
    isEmbedded,
    isSdkInitializing,
    sdkError,
  ]);

  if (isEmbedded === false) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h1>Access Denied</h1>
        <p>
          This service can only be accessed from within Amazon Connect Agent
          Workspace.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h1>Calltree Agent Service</h1>
      <p>Status: {serviceStatus}</p>
      <p style={{ fontSize: "12px", color: "#666" }}>
        This is a background service that automatically launches the Calltree
        Agent App when contacts arrive.
      </p>
    </div>
  );
}
